import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  MenuItem,
  Select,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import styles from "./index.module.scss";
import { DATA_TYPE_OPTIONS } from "components/ai-chat/constants";
import Account from "./Account";
import { toast } from "react-toastify";
import { useAxios } from "myAxios";
import {
  createClassicAccountTag,
  getClassicAccountDialogRows,
  getNextClassicAccountDialogRowsBySelectedList,
  useAppActions,
  validateClassicAccountDialogRows,
} from "components/ai-chat/store/AppStore";
import { useModel } from "hooks/useModel";
import { InfoType } from "components/ai-chat/types";
import { getInfoTagSelectedList } from "components/ai-chat/utils/tags";

interface IProps {
  type?: "single" | "multiple";
  defaultTag?: InfoType[];
  disabled?: boolean;
  open: boolean;
  promptData?: any;
  close: () => void;
  onOk?: (e: InfoType[]) => void;
}

const AccountDialog = (props: IProps) => {
  const {
    defaultTag,
    open,
    close: onClose,
    promptData,
    type = "single",
    disabled = false,
    onOk,
  } = props;
  const axios = useAxios();
  const { selectedKey } = useModel("aichat");
  const [rows, setRows] = useState<InfoType[]>([]);
  const { getLoadedMessage, basicSendMessageFn, newChatFn } = useAppActions(axios);

  const maxRows = 20;

  const handleAddRow = () => {
    if (rows.length < maxRows) {
      setRows([...rows, createClassicAccountTag()]);
    }
  };

  useEffect(() => {
    setRows(getClassicAccountDialogRows(defaultTag, promptData));
  }, [defaultTag, open]);

  const handleDeleteRow = (index: number) => {
    setRows(rows.filter((row, i) => i !== index));
  };

  const validateRows = () => {
    const isValid = validateClassicAccountDialogRows(rows);
    if (!isValid) {
      toast.error("Please finish filling out the form correctly.");
    }
    return isValid;
  };
  const handleTypeChange = (index: number, event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setRows(getNextClassicAccountDialogRowsBySelectedList(rows, index, value));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      className={styles["add-customer-dialog"]}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
    >
      <DialogTitle className={styles["dialog-title"]}>
        Add Customer Information
        <IconButton
          onClick={onClose}
          className={styles["close-btn"]}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        className={styles["dialog-content"]}
      >
        {rows.map((row, index) => {
          const loadedOptions = getInfoTagSelectedList(row);

          return (
            <div
              key={index}
              className={styles["customer-row-card"]}
            >
              <div className={styles["row-header"]}>
                <Typography className={styles["row-label"]}>
                  <span className={styles["index-badge"]}>{index + 1}</span>
                  Customer Information
                </Typography>
                {type === "multiple" && (
                  <Button
                    className={styles["delete-btn"]}
                    onClick={() => handleDeleteRow(index)}
                    disabled={disabled}
                  >
                    Delete
                  </Button>
                )}
              </div>

              <div className={styles["input-group"]}>
                <Account
                  row={row}
                  setRows={setRows}
                  index={index}
                  disabled={disabled}
                />
                <Select
                  className={styles["field-input"]}
                  size="small"
                  multiple
                  displayEmpty
                  disabled={disabled}
                  value={loadedOptions}
                  onChange={(e) => handleTypeChange(index, e as SelectChangeEvent<string[]>)}
                  IconComponent={KeyboardArrowDownIcon}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <span className={styles["select-placeholder"]}>
                          Select Information Types
                        </span>
                      );
                    }
                    if (selected.length === DATA_TYPE_OPTIONS.length) {
                      return <span>Select All</span>;
                    }
                    return (
                      <span className={styles["select-value-ellipsis"]}>
                        {selected?.join(", ")}
                      </span>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 400,
                      },
                      className: styles["select-menu"],
                    },
                  }}
                >
                  <MenuItem value="__SELECT_ALL__">
                    <Checkbox
                      checked={loadedOptions.length === DATA_TYPE_OPTIONS.length}
                      indeterminate={
                        loadedOptions.length > 0 && loadedOptions.length < DATA_TYPE_OPTIONS.length
                      }
                      size="small"
                    />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {DATA_TYPE_OPTIONS.map((option) => {
                    return (
                      <MenuItem
                        key={option}
                        value={option}
                      >
                        <Checkbox
                          checked={loadedOptions.includes(option)}
                          size="small"
                        />
                        <ListItemText primary={option} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </div>
            </div>
          );
        })}

        {type === "multiple" && (
          <Button
            fullWidth
            className={styles["add-row-btn"]}
            onClick={handleAddRow}
            startIcon={<AddCircleOutlineIcon />}
          >
            Add Customer Information ({rows.length}/20)
          </Button>
        )}
      </DialogContent>

      <DialogActions className={styles["dialog-actions"]}>
        <Button
          onClick={onClose}
          variant="contained"
          className={styles["action-btn"] + " " + styles["cancel"]}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            if (validateRows()) {
              onOk?.(rows);
              onClose();
              let chatId = selectedKey;
              if (promptData) {
                if (!chatId) {
                  const res = await newChatFn();
                  chatId = res?.id ?? null;
                }
              }
              getLoadedMessage(rows, defaultTag || [], chatId).then((result) => {
                if (promptData) {
                  const { inputValue, setLocalInput, prompt } = promptData;
                  basicSendMessageFn(
                    inputValue,
                    setLocalInput,
                    rows,
                    chatId!,
                    prompt,
                    result?.content ?? undefined
                  );
                }
              });
            }
          }}
          variant="contained"
          className={styles["action-btn"] + " " + styles["confirm"]}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountDialog;
